/**
 * What the oracle's metrics panel says, as data.
 *
 * The panel is a list of rows; deciding what each row reads — the score with a
 * real minus sign, the dishes by name, the pace and the budget it was found
 * under, the notes when a result was cut short or the kitchen has changed
 * since — is all here, so it is tested without a DOM and the component only
 * lays it out.
 */

import type {
  DifficultyBasis,
  OracleAblations,
  OracleBaselineSummary,
  OracleMetrics,
  OracleResultCore,
  OracleStatus,
  OracleSummary,
  OracleVerdict,
  PlayerId,
} from '@overcooked/shared'
import { TICK_MS } from '@overcooked/shared'
import { seatIdentity } from '../render/theme'

/**
 * Said wherever the oracle's score is shown, word for word as the CLI's
 * `--help` says it: what the Oracle is (two chefs planned together, knowing
 * every order ahead, reacting at once), and so that its score is a ceiling
 * for coordination, not something to hold a person to.
 */
export const ORACLE_CAVEAT = 'Two perfect chefs who know every order and share one plan. Use its score as a ceiling, not a target.'

/**
 * The rows that stay in view on the oracle page: the score, how it stands
 * against the best possible, whether every order was served, the baseline
 * beside it, and the difficulty proxies. Every other row (the dishes and the
 * interference among them), the budget and both tables sit under a closed
 * "Details".
 */
export const HEADLINE_ROWS: readonly string[] = ['score', 'bound', 'orders', 'baseline', 'difficulty', 'handoffs', 'workload']

/** What the baseline is, in the words the CLI uses for it. */
export const BASELINE_ABOUT = 'first play, sees only the tickets on the rail'

/** The baseline row while its solve has not been stored yet. */
export const BASELINE_PENDING = 'Working…'

/** `p1` → `Red`, `p2` → `Blue`: the name the HUD gives the chef in that seat. */
export function chefName(id: PlayerId): string {
  const seat = /^p(\d+)$/.exec(id)
  return seat ? seatIdentity(Number(seat[1])).label : id
}

/** First letter up: the engine's term labels are written mid-sentence. */
const sentence = (text: string): string => text.charAt(0).toUpperCase() + text.slice(1)

/** `230`, `−10` (a real minus sign). */
const amount = (n: number): string => (n < 0 ? `−${Math.abs(n)}` : String(n))

/** `230 points`, `−10 points` (a real minus sign), `1 point`. */
export function points(n: number): string {
  return `${amount(n)} ${Math.abs(n) === 1 ? 'point' : 'points'}`
}

/** `1 hand-off`, `25 hand-offs`. */
const count = (n: number, one: string, many = `${one}s`): string => `${n} ${n === 1 ? one : many}`

/** The words for a status, as a badge or a status line shows it. */
export function statusLabel(status: OracleStatus): string {
  switch (status) {
    case 'ready':
      return 'Ready'
    case 'solving':
      return 'Working…'
    case 'failed':
      return 'Failed'
    case 'impossible':
      return 'No solution'
  }
}

export interface SummaryRow {
  /** Stable, for tests and `data-row`. */
  id: string
  label: string
  value: string
  /** The internal names behind the value (`stepTicks 9`), shown small after it for staff. */
  staff?: string
}

/** A ratio or a rate to `places` decimals, with a real minus sign; `—` for none. */
export function fixed(x: number | null | undefined, places = 2): string {
  if (x === null || x === undefined || !Number.isFinite(x)) return '—'
  const text = Math.abs(x).toFixed(places)
  return x < 0 && Number(text) !== 0 ? `−${text}` : text
}

const signed = (n: number): string => (n > 0 ? `+${n}` : n < 0 ? `−${Math.abs(n)}` : '0')

/** `+12 points`, `0 points`, `−3 points`. */
const signedPoints = (n: number): string => `${signed(n)} ${Math.abs(n) === 1 ? 'point' : 'points'}`

const short = (hash: string): string => (/^[0-9a-f]{16,}$/.test(hash) ? hash.slice(0, 12) : hash)

/** `0:52`: ticks as minutes and seconds of the round. */
const clock = (ticks: number): string => {
  const seconds = Math.max(0, Math.round((ticks * TICK_MS) / 1000))
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`
}

/**
 * The verdict a result carries, or, for a result cached before verdicts
 * existed, the part of one its metrics can still give: the gap to the best
 * possible score. `null` when there is neither.
 */
function boundOf(result: OracleResultCore): Pick<OracleVerdict, 'provedBest' | 'gap'> | null {
  if (result.verdict) return result.verdict
  const ceiling = result.metrics?.ceiling
  if (ceiling === undefined) return null
  return { provedBest: result.score === ceiling, gap: Math.max(0, ceiling - result.score) }
}

/**
 * `Proved best possible`, or `Within 3 points of the best possible (99%)`.
 * Never "best" without the proof: short of it, the percentage stops at 99.
 */
export function boundText(result: OracleResultCore): string | null {
  const bound = boundOf(result)
  if (!bound) return null
  if (bound.provedBest) return 'Proved best possible'
  const ceiling = result.score + bound.gap
  const share = ceiling > 0 ? Math.min(99, Math.max(0, Math.round((result.score / ceiling) * 100))) : 0
  return `Within ${points(bound.gap)} of the best possible (${share}%)`
}

/**
 * `9 of 9 served · slowest out 0:52 after it arrived · 412 moves, 14 hand-offs`.
 * A missed order reads `8 of 9 served`, never "impossible"; a slot a full rail
 * turned away adds `1 skipped on a full rail`. A result cached before verdicts
 * existed says what it served and missed instead.
 */
export function ordersText(result: OracleResultCore): string {
  const v = result.verdict
  const effort = [count(result.steps, 'move'), ...(result.metrics ? [count(result.metrics.handoffs, 'hand-off')] : [])].join(', ')
  if (!v) {
    const served = Object.values(result.servedByDish).reduce((total, n) => total + n, 0)
    return `${served} served, ${result.expired} missed · ${effort}`
  }
  const parts = [`${v.ordersServed} of ${v.ordersIssued} served`]
  if (v.ordersSkipped) parts.push(`${v.ordersSkipped} skipped on a full rail`)
  if (v.worstWaitTicks !== null) parts.push(`slowest out ${clock(v.worstWaitTicks)} after it arrived`)
  parts.push(effort)
  return parts.join(' · ')
}

/**
 * `211 · first play, sees only the tickets on the rail · knowing ahead is
 * worth +49`, or `Working…` while its solve is pending. Score and
 * gain only: the baseline has no difficulty of its own.
 */
export function baselineText(baseline: OracleBaselineSummary, oracleScore: number): string {
  if (baseline.status === 'solving') return BASELINE_PENDING
  if (baseline.score === undefined) return statusLabel(baseline.status)
  return (
    `${amount(baseline.score)}${baseline.truncated ? ' (partial)' : ''} · ${BASELINE_ABOUT} · ` +
    `knowing ahead is worth ${signed(oracleScore - baseline.score)}`
  )
}

/** `1 move every 180 ms`: how fast the oracle walks. */
export function paceText(settings: OracleResultCore['settings']): string {
  return `1 move every ${settings.stepMs} ms`
}

/** `stepTicks 9`, or `stepTicks 4 (--step-ticks)` when the CLI overrode the kitchen's pace. */
export function paceStaff(settings: OracleResultCore['settings']): string {
  return settings.stepSource === 'cli' ? `stepTicks ${settings.stepTicks} (--step-ticks)` : `stepTicks ${settings.stepTicks}`
}

/** `881 of 4000 positions searched · gives up after 60 s`, and a reaction delay when there was one. */
export function searchText(result: OracleResultCore): string {
  const s = result.settings
  return (
    (result.expansions === undefined
      ? `Up to ${s.maxExpansions} positions`
      : `${result.expansions} of ${s.maxExpansions} positions searched`) +
    ` · gives up after ${Math.round(s.budgetMs / 1000)} s` +
    (s.reactionTicks > 0 ? ` · reacting ${s.reactionTicks * TICK_MS} ms late` : '')
  )
}

/**
 * The rows for a solved round. `dishLabel` names a dish id the way the HUD
 * does; without it the id is shown. `baseline` is the summary's baseline,
 * when it has one (a draft check has none, and gets no baseline row).
 */
export function summaryRows(
  result: OracleResultCore,
  dishLabel: (id: string) => string = (id) => id,
  baseline?: OracleBaselineSummary,
): SummaryRow[] {
  const served = Object.entries(result.servedByDish).filter(([, n]) => n > 0)
  const dishes = served.reduce((total, [, n]) => total + n, 0)
  const s = result.settings
  const t = result.trace
  const bound = boundText(result)
  const rows: SummaryRow[] = [
    { id: 'score', label: 'Score', value: points(result.score) },
    ...(bound === null ? [] : [{ id: 'bound', label: 'Best possible', value: bound }]),
    { id: 'orders', label: 'All orders', value: ordersText(result) },
    ...(baseline ? [{ id: 'baseline', label: 'Baseline', value: baselineText(baseline, result.score) }] : []),
    {
      id: 'dishes',
      label: 'Dishes served',
      value:
        dishes === 0
          ? 'none'
          : `${dishes} (${served.map(([id, n]) => `${dishLabel(id)} ${n}`).join(', ')})`,
    },
    { id: 'expired', label: 'Orders missed', value: String(result.expired) },
    { id: 'moves', label: 'Moves (both chefs)', value: String(result.steps) },
  ]
  if (result.metrics) rows.push(...metricRows(result.metrics, dishLabel))
  if (result.ablations) rows.push(...ablationRows(result.ablations))
  if (t) {
    rows.push({
      id: 'settled',
      label: 'Clashes settled',
      value:
        `${t.cycles} head-on (${t.jointPlans} joint, ${t.yieldFallbacks} yield) · ` +
        `${t.resourceCycles} over a counter or pan · ${t.aborts} replanned`,
    })
  }
  rows.push(
    { id: 'pace', label: 'Pace', value: paceText(s), staff: paceStaff(s) },
    {
      id: 'search',
      label: 'Search limit',
      value: searchText(result),
      staff: `maxExpansions ${s.maxExpansions} · beam ${s.beamWidth}`,
    },
    { id: 'policy', label: 'Best plan', value: result.policy },
    { id: 'solver', label: 'Version', value: `oracle ${short(s.solverVersion)} · kitchen ${short(s.contentHash)}` },
  )
  return rows
}

/**
 * How the work was shared, by actions: `Red 49% · Blue 51%`. The last chef
 * takes what rounding leaves, so the shares add up to 100.
 */
export function workloadSplit(m: OracleMetrics): string {
  const players = Object.keys(m.actions ?? {}).sort()
  const total = players.reduce((sum, p) => sum + (m.actions[p] ?? 0), 0)
  if (players.length === 0 || total === 0) return '—'
  let left = 100
  return players
    .map((p, i) => {
      const share = i === players.length - 1 ? left : Math.round(((m.actions[p] ?? 0) / total) * 100)
      left -= share
      return `${chefName(p)} ${share}%`
    })
    .join(' · ')
}

/**
 * The difficulty proxies as rows. The headline ones (the hand-offs, the
 * workload, the composite) carry one plain number each; the breakdown behind
 * each, and the best possible score itself, sit in their own rows under
 * Details.
 */
export function metricRows(m: OracleMetrics, dishLabel: (id: string) => string = (id) => id): SummaryRow[] {
  const forced = Object.entries(m.forcedHandoffsPerDish)
  return [
    {
      id: 'ceiling',
      label: 'Best possible score',
      value: `${points(m.ceiling)} · no round on this kitchen can score more`,
    },
    {
      id: 'gain',
      label: 'Search gain',
      value: `${signedPoints(m.searchGain)} over the best simple plan (${points(m.portfolioScore)})`,
    },
    {
      id: 'variety',
      label: 'Dish mix',
      value: `${m.simpleServed} simple / ${m.complexServed} complex · ${count(m.distinctDishesServed, 'kind')}`,
    },
    { id: 'handoffs', label: 'Hand-offs', value: count(m.handoffs, 'hand-off') },
    {
      id: 'handoffs-detail',
      label: 'Hand-offs in detail',
      value:
        `${m.counterHandoffs} over a counter, ${m.stationHandoffs} at a station · ${fixed(m.perDish.handoffs)} per dish · ` +
        `${m.wastedHandoffs} wasted · ${count(m.handoffWaits, 'wait')}`,
    },
    {
      id: 'forced',
      label: 'Hand-offs the layout forces',
      value: forced.length === 0 ? 'None' : forced.map(([id, n]) => `${dishLabel(id)} ${n}`).join(', '),
    },
    {
      id: 'interference',
      label: 'Interference',
      // A summary stored before interference replaced `conflicts` has none.
      value: m.interference === undefined ? '—' : `${count(m.interference, 'step')} in each other's way`,
    },
    { id: 'interference-detail', label: 'Interference per dish', value: fixed(m.perDish?.interference) },
    {
      id: 'crossings',
      label: 'Crossings',
      value: `${m.crossings} · ${fixed(m.perDish.crossings)} per dish`,
    },
    { id: 'workload', label: 'Workload', value: workloadSplit(m) },
    {
      id: 'workload-detail',
      label: 'Workload in detail',
      value: `imbalance ${fixed(m.imbalance)} · ${fixed(m.perDish.actions, 1)} actions per dish · idle ${Math.round(m.idleShare * 100)}% of the time`,
    },
    { id: 'specialization', label: 'Specialization', value: fixed(m.specialization) },
    { id: 'overlap', label: 'Dishes in parallel', value: fixed(m.overlap) },
    {
      id: 'difficulty',
      label: 'Difficulty',
      value: m.difficulty === null ? 'Not rated (search stopped early)' : fixed(m.difficulty),
    },
  ]
}

/**
 * The ablations as rows, when the result carries them: the kitchen without
 * hand-offs, each chef alone, and a chef that takes a moment to react. What
 * knowing every order is worth is the baseline's row, not an ablation's.
 */
export function ablationRows(a: OracleAblations): SummaryRow[] {
  const h = a.requiredHandoffs
  const solo = Object.entries(a.soloScores).sort(([x], [y]) => (x < y ? -1 : x > y ? 1 : 0))
  const r = a.reaction
  return [
    {
      id: 'ablation-handoffs',
      label: 'Without hand-offs',
      value:
        `${points(h.scoreWithout)} (${h.servedWithout} served) · ` +
        (h.required ? 'hand-offs required: no dish without one' : 'hand-offs not required'),
    },
    {
      id: 'ablation-team',
      label: 'One chef alone',
      value:
        solo.length === 0
          ? 'Only one chef'
          : `${solo.map(([id, n]) => `${chefName(id)} ${points(n)}`).join(', ')} · ` +
            (a.teamAdvantage === null ? 'no chef scores alone' : `team advantage ×${fixed(a.teamAdvantage)}`),
    },
    {
      id: 'ablation-reaction',
      label: 'Reacting like a person',
      value: `${r.reactionMs} ms late: ${points(r.score)} (instant ${points(r.instantScore)}, ${signed(-r.drop)})`,
    },
  ]
}

export interface ChefTable {
  players: PlayerId[]
  rows: Array<{ id: 'steps' | 'actions' | 'idle' | 'partner' | 'station'; label: string; values: string[] }>
}

/**
 * One column per chef: steps, actions, idle steps, and steps waiting for the
 * partner or for a station. A summary stored before the waits were split (it
 * had `waitForSpaceSteps`) has neither, and shows `—` in those rows.
 */
export function chefTable(m: OracleMetrics): ChefTable {
  const players = Object.keys(m.steps).sort()
  const col = (record: Record<PlayerId, number> | undefined): string[] =>
    players.map((p) => (record === undefined ? '—' : String(record[p] ?? 0)))
  return {
    players,
    rows: [
      { id: 'steps', label: 'Moves', values: col(m.steps) },
      { id: 'actions', label: 'Actions', values: col(m.actions) },
      { id: 'idle', label: 'Idle', values: col(m.idleSteps) },
      { id: 'partner', label: 'Waiting for partner', values: col(m.waitForPartnerSteps) },
      { id: 'station', label: 'Waiting for a station', values: col(m.waitForStationSteps) },
    ],
  }
}

export interface CompositeTable {
  /** "ref-v1": the reference set's version, shown small for staff. */
  reference: string
  /** "against 5 reference kitchens". */
  against: string
  rows: Array<{ id: string; label: string; value: string; z: string; weight: string }>
}

/** The composite's working: each term's value, how far it is from typical (a z-score against the reference), and its weight. */
export function compositeTable(basis: DifficultyBasis): CompositeTable {
  return {
    reference: basis.referenceVersion,
    against: `against ${count(basis.referenceMaps.length, 'reference kitchen')}`,
    rows: basis.terms.map((t) => ({
      id: t.id,
      label: sentence(t.label),
      value: fixed(t.value),
      z: fixed(t.z),
      weight: String(t.weight),
    })),
  }
}

/** Notes that change how far the number can be trusted, in the order they matter. */
export function summaryNotes(summary: OracleSummary, headerContentHash: string | null): string[] {
  const notes: string[] = []
  const result = summary.result
  if (result?.truncated) {
    notes.push('Stopped early by the time limit. This is the best round found in time.')
  }
  if (result?.expansionsExhausted) {
    notes.push(
      `Hit the search limit (${result.settings.maxExpansions}) before the round ended. A bigger limit might find a better round.`,
    )
  }
  if (headerContentHash !== null && headerContentHash !== summary.contentHash) {
    notes.push('The recipes, timings or layout have changed since this round was solved.')
  }
  if (summary.status === 'failed') {
    const failure = summary.failure
    notes.push(
      `The oracle failed${failure ? ` after ${failure.attempts} attempt${failure.attempts === 1 ? '' : 's'}: ${failure.error}` : '.'}`,
    )
  }
  return notes
}
